import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/client";
import type {
  CatalogCard,
  ItemDefinition,
  PackCard,
  PackStack,
  User,
} from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { Card, naturalCardWidthPx } from "../components/Card";
import { CardTilt, type CardTiltHandle } from "../components/CardTilt";
import { Inspector } from "../components/Inspector";
import { catalogToCardView } from "../logic/catalogToCardView";
import { playSfx } from "../logic/sfx";
import { pixelTheme } from "../theme/pixel";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { E32 } from "../theme/endesga32";
import { RARITIES } from "../theme/rarities";
import { useActiveGameRedirect } from "../logic/useActiveGameRedirect";
import { useCatalogAndCollection } from "../api/useCatalog";
import { useGuestRedirect } from "../logic/useGuestRedirect";

// Common skips the buildup entirely and flips on click.
const PULSE_COUNT: Record<PackStack["rarity"], number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  legendary: 3,
};
// Climbs small → medium → large with each pulse, regardless of card
// rarity (rarity controls how many pulses fire).
const PULSE_SFX = [
  "ai_orchestral_hit_lute_small",
  "ai_orchestral_hit_lute_medium",
  "ai_orchestral_hit_lute_large",
] as const;
const PULSE_DURATION = 480;
const PULSE_INTERVAL = 540;
const TEAR_DURATION = 700;
const FLY_DURATION = 380;
// Pulse apex lands at 40% of duration; SFX + color snaps fire here.
const PULSE_PEAK_OFFSET_MS = PULSE_DURATION * 0.4;
// _50 values are the inter-pulse / final hold; _100 are pulse apex.
const TIER_COLORS = [
  RARITIES.uncommon.color,
  RARITIES.rare.color,
  RARITIES.legendary.color,
] as const;
const TIER_BLUR_50 = [10, 18, 28] as const;
const TIER_BLUR_100 = [20, 36, 56] as const;

interface BuildupPlan {
  /** Keyframe name referenced from `animation`. Stable per rarity. */
  name: string;
  /** Total animation duration ms — matches the flip-time scheduling. */
  durationMs: number;
  /** Generated @keyframes block — inject inside a <style> tag. */
  css: string;
  /** Static filter to apply once the animation ends. Matches the final
   *  keyframe so the handover into flipped/flying has no visual jump. */
  finalFilter: string;
}

// Returns null for common (no pulses). Each pulse holds the previous
// tier's color through grow, snaps to the new color at apex, then
// shrinks to that tier's 50% hold.
function buildupPlan(rarity: PackStack["rarity"]): BuildupPlan | null {
  const pulses = PULSE_COUNT[rarity];
  if (pulses === 0) return null;
  const total = PULSE_INTERVAL * pulses + PULSE_DURATION;
  type Frame = { t: number; scale: number; blur: number; color: string };
  const frames: Frame[] = [];
  // Lead-in: blur 0 reads as "no glow"; tier-0 color avoids stray tint
  // during the ramp into pulse 1.
  frames.push({ t: 0, scale: 1, blur: 0, color: TIER_COLORS[0] });
  for (let i = 0; i < pulses; i++) {
    const start = PULSE_INTERVAL * (i + 1);
    const peak = start + PULSE_PEAK_OFFSET_MS;
    const end = start + PULSE_DURATION;
    const newColor = TIER_COLORS[i];
    const prevColor = i === 0 ? TIER_COLORS[0] : TIER_COLORS[i - 1];
    const startBlur = i === 0 ? 0 : TIER_BLUR_50[i - 1];
    const peakBlur = TIER_BLUR_100[i];
    const endBlur = TIER_BLUR_50[i];
    frames.push({ t: start, scale: 1, blur: startBlur, color: prevColor });
    // Hold prevColor through grow, snap to newColor at peak.
    frames.push({ t: peak - 1, scale: 1.15, blur: peakBlur, color: prevColor });
    frames.push({ t: peak, scale: 1.15, blur: peakBlur, color: newColor });
    frames.push({ t: end, scale: 1, blur: endBlur, color: newColor });
  }
  const name = `buildup-${rarity}`;
  const lines = frames.map(({ t, scale, blur, color }) => {
    const pct = ((t / total) * 100).toFixed(4);
    return `  ${pct}% { transform: scale(${scale}); filter: drop-shadow(0 0 ${blur}px ${color}); }`;
  });
  const lastTier = pulses - 1;
  return {
    name,
    durationMs: total,
    css: `@keyframes ${name} {\n${lines.join("\n")}\n}`,
    finalFilter: `drop-shadow(0 0 ${TIER_BLUR_50[lastTier]}px ${TIER_COLORS[lastTier]})`,
  };
}

type StackId = number;

interface Stack {
  id: StackId;
  /** The cards in this stack. Length 1 (singleton — legendary wildcard)
   *  or 2 (a pair: two copies of the same card_id). The reveal shows
   *  the single identity face-up; when it's a pair, an offset duplicate
   *  is layered behind to indicate the player got two copies. */
  cards: PackCard[];
  rarity: PackStack["rarity"];
}

type Phase =
  | { kind: "idle" }
  | { kind: "tearing" }
  | { kind: "revealing"; index: number; sub: SubPhase }
  | { kind: "done" };

type SubPhase =
  | { kind: "face_down" }
  | { kind: "buildup" }
  | { kind: "flipped" }
  | { kind: "flying" };

export function PackOpeningPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { itemId = "" } = useParams<{ itemId: string }>();
  useActiveGameRedirect();
  useGuestRedirect("packs");

  const [user, setUser] = useState<User | null>(null);
  // Shared catalog cache — already warm if any other page loaded it.
  // `openPack` invalidates the cache after each open, so freshly
  // granted cards render face-up without an explicit refetch here.
  const { catalogById } = useCatalogAndCollection();
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [stacks, setStacks] = useState<Stack[]>([]);
  const [revealed, setRevealed] = useState<PackCard[]>([]);
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // How many of `itemId` the user holds in inventory. Read on mount
  // from `getInventory`, then maintained from each open's
  // `remaining_in_inventory`. Drives the IDLE / DONE affordances.
  const [inventoryCount, setInventoryCount] = useState<number>(0);
  const [packItem, setPackItem] = useState<ItemDefinition | null>(null);
  // Set true the first time `reloadInventory` finishes (success or
  // not-found). Gates all idle-state UI so the page doesn't briefly
  // paint with the default palette / "no packs" fallback before the
  // real item definition arrives.
  const [inventoryLoaded, setInventoryLoaded] = useState(false);

  // Staggered setTimeouts cancelled on skip-click.
  const timersRef = useRef<number[]>([]);
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;

  // Hero DOM node — used as the FLIP source rect for the grid fly-in.
  const heroRef = useRef<HTMLDivElement | null>(null);
  const flyOriginRef = useRef<DOMRect | null>(null);
  // Hero tilt handle — flyToGrid resets it to 0 before measuring.
  const heroTiltRef = useRef<CardTiltHandle | null>(null);

  function clearTimers() {
    for (const id of timersRef.current) window.clearTimeout(id);
    timersRef.current = [];
  }

  const reloadUser = useCallback(async () => {
    try {
      const r = await api.getMe();
      setUser(r.user);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [navigate]);

  // Read inventory once on mount to seed the count + item definition.
  // Subsequent counts come from the server's `remaining_in_inventory`
  // on each open, so we don't need to refetch the whole inventory.
  const reloadInventory = useCallback(async () => {
    try {
      const inv = await api.getInventory();
      const item = inv.items.find((i) => i.id === itemId) ?? null;
      setPackItem(item);
      const total = inv.slots.reduce(
        (sum, s) => (s.item_id === itemId ? sum + s.count : sum),
        0,
      );
      setInventoryCount(total);
      setInventoryLoaded(true);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setInventoryLoaded(true);
    }
  }, [itemId, navigate]);

  useEffect(() => {
    void reloadUser();
    void reloadInventory();
    return () => clearTimers();
  }, [reloadUser, reloadInventory]);

  // Pack response is server-ordered: each stack is one reveal step,
  // pair stacks repeat the same card_id, singletons (legendary
  // wildcard) carry one id.
  function buildStacks(input: PackStack[]): Stack[] {
    return input.map((s, i) => ({ id: i, cards: s.cards, rarity: s.rarity }));
  }

  const onOpenPack = useCallback(async () => {
    if (busy || !itemId) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await api.openPack(itemId);
      setStacks(buildStacks(resp.stacks));
      setInventoryCount(resp.remaining_in_inventory);
      // Preserve `revealed` so the grid carries earlier opens across packs.
      setPhase({ kind: "tearing" });
      playSfx("ai_pack_rip_open_sound");
      const t = window.setTimeout(() => {
        setPhase({ kind: "revealing", index: 0, sub: { kind: "face_down" } });
      }, TEAR_DURATION);
      timersRef.current.push(t);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setPhase({ kind: "idle" });
    } finally {
      setBusy(false);
    }
  }, [busy, itemId, navigate]);

  function startBuildup(stack: Stack) {
    if (stack.rarity === "common") {
      playSfx("ai_paper_flutter");
      setPhase((p) =>
        p.kind === "revealing" ? { ...p, sub: { kind: "flipped" } } : p,
      );
      return;
    }
    const pulses = PULSE_COUNT[stack.rarity];
    setPhase((p) =>
      p.kind === "revealing" ? { ...p, sub: { kind: "buildup" } } : p,
    );
    // Chimes fire at each pulse peak (same moment the color snaps).
    for (let i = 0; i < pulses; i++) {
      const peakAt = PULSE_INTERVAL * (i + 1) + PULSE_PEAK_OFFSET_MS;
      const t = window.setTimeout(() => {
        if (phaseRef.current.kind !== "revealing") return;
        playSfx(PULSE_SFX[Math.min(i, 2) as 0 | 1 | 2]);
      }, peakAt);
      timersRef.current.push(t);
    }
    const flipAt = PULSE_INTERVAL * pulses + PULSE_DURATION;
    const tFlip = window.setTimeout(() => {
      if (phaseRef.current.kind !== "revealing") return;
      playSfx("ai_paper_flutter");
      setPhase((p) =>
        p.kind === "revealing" ? { ...p, sub: { kind: "flipped" } } : p,
      );
    }, flipAt);
    timersRef.current.push(tFlip);
  }

  function skipToFlipped(_stack: Stack) {
    clearTimers();
    playSfx("ai_paper_flutter");
    setPhase((p) =>
      p.kind === "revealing" ? { ...p, sub: { kind: "flipped" } } : p,
    );
  }

  function flyToGrid(stack: Stack) {
    clearTimers();
    // Snap tilt to 0 before measuring so the ghost spawn matches the
    // un-tilted layout box.
    heroTiltRef.current?.reset();
    // Capture the click-time rect before mutating state — that's the
    // FLIP origin the grid's useLayoutEffect reads.
    flyOriginRef.current = heroRef.current?.getBoundingClientRect() ?? null;
    setRevealed((r) => [...r, ...stack.cards]);
    setPhase((p) =>
      p.kind === "revealing" ? { ...p, sub: { kind: "flying" } } : p,
    );
    const t = window.setTimeout(() => {
      const nextIdx = (phaseRef.current.kind === "revealing"
        ? phaseRef.current.index
        : 0) + 1;
      if (nextIdx >= stacks.length) {
        setPhase({ kind: "done" });
      } else {
        setPhase({
          kind: "revealing",
          index: nextIdx,
          sub: { kind: "face_down" },
        });
      }
    }, FLY_DURATION);
    timersRef.current.push(t);
  }

  function onStackClick() {
    if (phase.kind !== "revealing") return;
    const stack = stacks[phase.index];
    if (!stack) return;
    if (phase.sub.kind === "face_down") {
      startBuildup(stack);
      return;
    }
    if (phase.sub.kind === "buildup") {
      skipToFlipped(stack);
      return;
    }
    if (phase.sub.kind === "flipped") {
      flyToGrid(stack);
      return;
    }
    // Flying: ignore until landing.
  }

  function onOpenAnother() {
    // Return to idle. `revealed` persists across packs.
    setPhase({ kind: "idle" });
    setStacks([]);
  }

  const remaining = inventoryCount;
  const hasPacks = remaining > 0;
  // Render the item-name breadcrumb from the URL slug up front so the
  // header is correct on the first paint — the loaded `packItem.name`
  // takes over once it arrives.
  const breadcrumb = packItem
    ? `${packItem.name.toUpperCase()}`
    : slugToBreadcrumb(itemId);
  const packFaction =
    packItem?.use?.kind === "rip_open_pack" ? packItem.use.faction ?? null : null;
  const palette = packPaletteFor(packFaction);

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "24px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
        <AppHeader
          user={user}
          onBack={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/lobby");
          }}
        />
        <Breadcrumb text={breadcrumb} />
      </div>
      {error && (
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            width: "100%",
            padding: 12,
            background: theme.panel,
            border: `2px solid ${theme.accent}`,
            color: theme.accent,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 32,
          paddingTop: 24,
          maxWidth: 1100,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {/* Fixed-height phase area so transitions between idle / tearing
         *  / revealing / done don't reflow the page. Pinned (no shrink)
         *  so the hero card stays put while the grid below scrolls. */}
        <div
          style={{
            minHeight: PHASE_AREA_HEIGHT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {/* Wait for the inventory load to complete before rendering
           *  any idle-state UI. Two prior flashes lived here:
           *
           *  1. The default (navy) palette painting for one tick before
           *     swapping to e.g. goblin scarlet.
           *  2. The hasPacks=false "no packs of this kind" message and
           *     BACK button briefly appearing before the real count
           *     arrived.
           *
           *  Both are gated by `inventoryLoaded`. The phase area has a
           *  reserved minHeight so the blank slot doesn't reflow. */}
          {phase.kind === "idle" && inventoryLoaded && (
            <IdleStage
              theme={theme}
              hasPacks={hasPacks}
              busy={busy}
              onOpen={onOpenPack}
              onBack={() => navigate("/lobby")}
              palette={palette}
            />
          )}
          {phase.kind === "tearing" && (
            <TearStage theme={theme} palette={palette} />
          )}
          {phase.kind === "revealing" && stacks[phase.index] && (
            <RevealStage
              theme={theme}
              stack={stacks[phase.index]}
              sub={phase.sub}
              catalogById={catalogById}
              onClick={onStackClick}
              heroRef={heroRef}
              heroTiltRef={heroTiltRef}
              remainingBehind={stacks.length - phase.index - 1}
            />
          )}
          {phase.kind === "done" && (
            <DoneStage
              theme={theme}
              remaining={remaining}
              onOpenAnother={onOpenAnother}
              onBack={() => navigate("/lobby")}
            />
          )}
        </div>
        {/* Scrollable grid region. The phase area above stays pinned;
         *  only this region scrolls. Cards flying in from the deck do
         *  not get clipped by this overflow because the fly-in is
         *  rendered as a fixed-positioned ghost at document.body via
         *  React portal — the slot itself stays invisible until the
         *  ghost lands. Horizontal padding gives the NEW badges' -6px
         *  overhang room. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "12px 8px 24px",
            boxSizing: "border-box",
          }}
        >
          <RevealGrid
            theme={theme}
            revealed={revealed}
            catalogById={catalogById}
            flyOriginRef={flyOriginRef}
            onInspect={(c) => {
              playSfx("ai_paper_flutter");
              setInspect(c);
            }}
          />
        </div>
      </div>
      <Inspector card={inspect} theme={theme} onClose={() => setInspect(null)} />
    </div>
  );
}

function IdleStage({
  theme,
  hasPacks,
  busy,
  onOpen,
  onBack,
  palette,
}: {
  theme: typeof pixelTheme;
  hasPacks: boolean;
  busy: boolean;
  onOpen: () => void;
  onBack: () => void;
  palette: PackPalette;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
      }}
    >
      <PackArt
        theme={theme}
        animated={hasPacks}
        onClick={hasPacks && !busy ? onOpen : undefined}
        palette={palette}
      />
      {hasPacks ? (
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.inkFaint,
          }}
        >
          {busy ? "OPENING…" : "CLICK TO OPEN"}
        </div>
      ) : (
        <>
          <div style={{ color: theme.inkFaint, fontSize: 14 }}>
            No packs of this kind in your inventory. Earn Void Juice
            from challenges, then buy a pack at the Cardmaster.
          </div>
          <button onClick={onBack} {...primaryButton(theme, false)}>
            BACK TO LOBBY
          </button>
        </>
      )}
    </div>
  );
}

function TearStage({
  theme,
  palette,
}: {
  theme: typeof pixelTheme;
  palette: PackPalette;
}) {
  return (
    <div style={{ position: "relative", width: 220, height: 320 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 220,
          height: 160,
          ...packPanelStyle(palette),
          borderBottom: `2px dashed ${theme.panelEdgeStrong}`,
          animation: "pack-top 700ms ease-out forwards",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 160,
          left: 0,
          width: 220,
          height: 160,
          ...packPanelStyle(palette),
          borderTop: `2px dashed ${theme.panelEdgeStrong}`,
          animation: "pack-bot 700ms ease-out forwards",
        }}
      />
      <style>{`
        @keyframes pack-top {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(-60px) rotate(-6deg); opacity: 0; }
        }
        @keyframes pack-bot {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(60px) rotate(4deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function RevealStage({
  theme,
  stack,
  sub,
  catalogById,
  onClick,
  heroRef,
  heroTiltRef,
  remainingBehind,
}: {
  theme: typeof pixelTheme;
  stack: Stack;
  sub: SubPhase;
  catalogById: Map<string, CatalogCard>;
  onClick: () => void;
  heroRef: React.MutableRefObject<HTMLDivElement | null>;
  heroTiltRef: React.MutableRefObject<CardTiltHandle | null>;
  remainingBehind: number;
}) {
  const flipped = sub.kind === "flipped" || sub.kind === "flying";
  const flying = sub.kind === "flying";
  const inBuildup = sub.kind === "buildup";
  const isPair = stack.cards.length >= 2;
  // Pair stacks: front card is real, back is a visual duplicate.
  const front = stack.cards[0];
  const frontCard = front ? catalogById.get(front.card_id) : undefined;
  if (!frontCard) return null;
  // Static filter at flipped/flying must match the animation's last
  // keyframe so the handover doesn't visually jump.
  const plan = buildupPlan(stack.rarity);
  const heroAnimation =
    inBuildup && plan
      ? `${plan.name} ${plan.durationMs}ms linear forwards`
      : "none";
  const heroStaticFilter =
    !inBuildup && flipped && plan ? plan.finalFilter : "none";

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Reveal next card"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: "pointer", position: "relative" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "inline-block",
            width: naturalCardWidthPx("hero"),
          }}
        >
          {/* The deck — a single face-down card at the same position as
           *  the hero. Rendered whenever there's still at least one
           *  more stack to reveal *after* this one, so when the hero
           *  flies to the grid, the deck remains visible underneath
           *  (you're drawing the next card off the top of it).
           *  Earlier in DOM than the hero, so the hero paints on top
           *  in normal flow. */}
          {remainingBehind > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                pointerEvents: "none",
              }}
            >
              <Card
                card={catalogToCardView(frontCard)}
                theme={theme}
                size="hero"
                faceDown={true}
              />
            </div>
          )}
          {/* Hero card. During buildup, the rarity-specific keyframe
           *  drives both scale (the pulse) and filter (the climbing-
           *  tier glow) on this wrapper. After buildup, the static
           *  filter holds the final 50% glow. During flying, the
           *  inner ref'd div hides via `visibility: hidden` so only
           *  the FLIP'd grid copy is visible mid-flight. */}
          <div
            style={{
              position: "relative",
              animation: heroAnimation,
              filter: inBuildup ? undefined : heroStaticFilter,
            }}
          >
            <div
              ref={heroRef}
              style={{
                position: "relative",
                visibility: flying ? "hidden" : "visible",
                // Scale 1.15 only in flipped — flying takes over with
                // visibility:hidden, so the 1.15→1 transition isn't
                // visible to the user (no "pulsing deck" artifact).
                transform: sub.kind === "flipped" ? "scale(1.15)" : "scale(1)",
                transformOrigin: "center center",
                transition: "transform 240ms cubic-bezier(.2,.9,.2,1)",
              }}
            >
              {/* CardTilt only kicks in once the card is flipped —
                  no point tilting the face-down card during buildup,
                  and the FLIP measurement uses heroRef (un-transformed
                  ancestor) so the tilt's inner rotation doesn't
                  inflate the measured rect. Disabled for non-flipped
                  states so face_down / buildup keep their existing
                  layout exactly. */}
              <CardTilt enabled={flipped} ref={heroTiltRef}>
                {flipped && isPair && (
                  <div
                    style={{
                      position: "absolute",
                      top: -10,
                      left: 14,
                      width: "100%",
                      transform: "rotate(3deg)",
                      filter: "brightness(0.85)",
                      pointerEvents: "none",
                    }}
                  >
                    <Card
                      card={catalogToCardView(frontCard)}
                      theme={theme}
                      size="hero"
                      faceDown={false}
                    />
                  </div>
                )}
                <Card
                  card={catalogToCardView(frontCard)}
                  theme={theme}
                  size="hero"
                  faceDown={!flipped}
                />
              </CardTilt>
            </div>
          </div>
        </div>
      </div>
      {/* Fixed-height label slot so transitions between sub-phases
       *  don't reflow the hero. Always reserves the same vertical
       *  space; the inner text appears only on `face_down` (the
       *  call-to-action). After flip we deliberately leave the slot
       *  empty — the rarity is already legible on the card frame
       *  itself, and an extra "PAIR COMMON"-style label felt
       *  redundant. The empty div keeps the next-card transition
       *  from reflowing the hero. */}
      <div
        style={{
          marginTop: 24,
          height: 14,
          textAlign: "center",
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        {sub.kind === "face_down" && (
          <span
            style={{
              display: "inline-block",
              animation:
                "cta-fadein 400ms ease-out, cta-pulse 2s ease-in-out 400ms infinite",
            }}
          >
            CLICK TO REVEAL
          </span>
        )}
      </div>
      <style>{`
        @keyframes cta-fadein {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes cta-pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.5; }
        }
        ${plan?.css ?? ""}
      `}</style>
    </div>
  );
}

function DoneStage({
  theme,
  remaining,
  onOpenAnother,
  onBack,
}: {
  theme: typeof pixelTheme;
  remaining: number;
  onOpenAnother: () => void;
  onBack: () => void;
}) {
  return (
    <div style={{ display: "flex", gap: 12 }}>
      {remaining > 0 && (
        <button onClick={onOpenAnother} {...primaryButton(theme, false)}>
          OPEN ANOTHER ({remaining})
        </button>
      )}
      <button onClick={onBack} {...primaryButton(theme, false)}>
        BACK TO LOBBY
      </button>
    </div>
  );
}

// New slots stay opacity 0 while a portal'd ghost flies from the hero
// rect to the slot rect (FLY_DURATION). Portal is required because
// the grid lives inside an overflow:auto wrapper that would clip the
// transform.
type Ghost = {
  key: string;
  slotIndex: number;
  card: CatalogCard;
  origin: DOMRect;
  dest: DOMRect;
};

function RevealGrid({
  theme,
  revealed,
  catalogById,
  flyOriginRef,
  onInspect,
}: {
  theme: typeof pixelTheme;
  revealed: PackCard[];
  catalogById: Map<string, CatalogCard>;
  flyOriginRef: React.MutableRefObject<DOMRect | null>;
  onInspect?: (card: CatalogCard) => void;
}) {
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevLenRef = useRef(0);
  const [ghosts, setGhosts] = useState<Ghost[]>([]);
  // Slot indices mid-flight — hidden until their ghost lands.
  const [flying, setFlying] = useState<Set<number>>(new Set());

  useLayoutEffect(() => {
    const prevLen = prevLenRef.current;
    if (revealed.length <= prevLen) {
      prevLenRef.current = revealed.length;
      return;
    }
    const origin = flyOriginRef.current;
    if (origin) {
      const newGhosts: Ghost[] = [];
      for (let i = prevLen; i < revealed.length; i++) {
        const el = slotRefs.current.get(i);
        if (!el) continue;
        const card = catalogById.get(revealed[i].card_id);
        if (!card) continue;
        const dest = el.getBoundingClientRect();
        if (dest.width === 0 || dest.height === 0) continue;
        newGhosts.push({
          key: `${i}-${performance.now()}`,
          slotIndex: i,
          card,
          origin,
          dest,
        });
      }
      if (newGhosts.length > 0) {
        const newIdxs = newGhosts.map((g) => g.slotIndex);
        setGhosts((prev) => [...prev, ...newGhosts]);
        setFlying((prev) => {
          const next = new Set(prev);
          for (const i of newIdxs) next.add(i);
          return next;
        });
        const ghostKeys = new Set(newGhosts.map((g) => g.key));
        window.setTimeout(() => {
          setGhosts((prev) => prev.filter((g) => !ghostKeys.has(g.key)));
          setFlying((prev) => {
            const next = new Set(prev);
            for (const i of newIdxs) next.delete(i);
            return next;
          });
        }, FLY_DURATION);
      }
    }
    flyOriginRef.current = null;
    prevLenRef.current = revealed.length;
  }, [revealed, flyOriginRef, catalogById]);

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 16,
          width: "100%",
        }}
      >
        {revealed.map((c, i) => {
          const card = catalogById.get(c.card_id);
          if (!card) return null;
          const isFlying = flying.has(i);
          return (
            <div
              key={i}
              ref={(el) => {
                if (el) slotRefs.current.set(i, el);
                else slotRefs.current.delete(i);
              }}
              role={onInspect && !isFlying ? "button" : undefined}
              tabIndex={onInspect && !isFlying ? 0 : undefined}
              aria-label={
                onInspect && !isFlying ? `Inspect ${card.name}` : undefined
              }
              onClick={onInspect && !isFlying ? () => onInspect(card) : undefined}
              onKeyDown={
                onInspect && !isFlying
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onInspect(card);
                      }
                    }
                  : undefined
              }
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                cursor: onInspect && !isFlying ? "pointer" : "default",
                opacity: isFlying ? 0 : 1,
                pointerEvents: isFlying ? "none" : "auto",
              }}
            >
              <div style={{ width: naturalCardWidthPx("small") }}>
                <Card card={catalogToCardView(card)} theme={theme} size="small" />
              </div>
              {c.is_new_to_collection && (
                <div
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    background: E32.cyan,
                    color: theme.bgDeep,
                    fontFamily: theme.fontDisplay,
                    fontSize: 9,
                    padding: "2px 6px",
                    border: `2px solid ${theme.bgDeep}`,
                  }}
                >
                  NEW
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ghosts.map((g) => (
        <FlyingGhost key={g.key} ghost={g} theme={theme} />
      ))}
    </>
  );
}

// FLIP-technique in-flight card: place at dest, snap inverse-back to
// origin without transition, force layout, then animate to identity.
// Portal'd to document.body to escape clipping ancestors.
function FlyingGhost({ ghost, theme }: { ghost: Ghost; theme: typeof pixelTheme }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { origin, dest } = ghost;
    const dx = origin.left - dest.left;
    const dy = origin.top - dest.top;
    const sx = origin.width / dest.width;
    const sy = origin.height / dest.height;
    el.style.transformOrigin = "top left";
    el.style.transition = "none";
    el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    void el.offsetWidth;
    el.style.transition = `transform ${FLY_DURATION}ms ease-out`;
    el.style.transform = "";
  }, [ghost]);
  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: ghost.dest.left,
        top: ghost.dest.top,
        width: ghost.dest.width,
        height: ghost.dest.height,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 999,
      }}
    >
      <div style={{ width: naturalCardWidthPx("small") }}>
        <Card card={catalogToCardView(ghost.card)} theme={theme} size="small" />
      </div>
    </div>,
    document.body,
  );
}

function PackArt({
  theme,
  animated,
  onClick,
  palette,
}: {
  theme: typeof pixelTheme;
  animated: boolean;
  onClick?: () => void;
  palette: PackPalette;
}) {
  // Crimped foil-seam strip; tiles horizontally to any width. Uses the
  // pack's trim color (one shade darker than the body) striped over the
  // body color, so the seam reads as part of the trim.
  const crimpStripe: React.CSSProperties = {
    height: PACK_CRIMP_HEIGHT,
    background: `repeating-linear-gradient(90deg, ${palette.trim} 0 2px, ${palette.bg} 2px 5px)`,
    borderTop: `2px solid ${palette.trim}`,
    borderBottom: `2px solid ${palette.trim}`,
  };
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? "Open pack" : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        width: 220,
        height: 320,
        ...packPanelStyle(palette),
        animation: animated ? "pack-bob 2.4s ease-in-out infinite" : undefined,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={crimpStripe} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: E32.ink,
            textAlign: "center",
          }}
        >
          DECK OF INFINITY
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 96,
            color: E32.ink,
            lineHeight: 1,
          }}
        >
          {"♞"}
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: E32.ink,
            textAlign: "center",
          }}
        >
          {palette.subtitle}
        </div>
      </div>
      <div style={crimpStripe} />
      <style>{`
        @keyframes pack-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

// Pack art palette. Only styles the unopened pack (idle / tearing);
// card backs stay black. Faction packs swap to their faction color so
// e.g. a goblin booster reads as red at a glance; the set-wide High
// Fantasia pack keeps the original deep-blue + silver scheme.
// Text + glyph are always ink (black) per design; only the pack's
// body fill and trim shift by faction. The trim ("border + crimp dark
// stripe") is one Endesga-32 entry darker than the body, so the pack
// reads as a single-hue object with one slightly darker accent.
interface PackPalette {
  bg: string;
  trim: string;
  /** Subtitle shown below the glyph (e.g. "GOBLINS" / "HIGH FANTASIA"). */
  subtitle: string;
}

const PACK_CRIMP_HEIGHT = 14;

const DEFAULT_PACK_PALETTE: PackPalette = {
  bg: E32.navy,
  trim: E32.midnight,
  subtitle: "HIGH FANTASIA",
};

const FACTION_PACK_PALETTES: Record<string, PackPalette> = {
  humans: { bg: E32.white, trim: E32.mist, subtitle: "HUMANS" },
  goblins: { bg: E32.scarlet, trim: E32.ruby, subtitle: "GOBLINS" },
  dwarves: { bg: E32.brick, trim: E32.oxblood, subtitle: "DWARVES" },
  orcs: { bg: E32.fern, trim: E32.forest, subtitle: "ORCS" },
  elves: { bg: E32.mulberry, trim: E32.plum, subtitle: "ELVES" },
};

function packPaletteFor(faction: string | null | undefined): PackPalette {
  if (!faction) return DEFAULT_PACK_PALETTE;
  return FACTION_PACK_PALETTES[faction] ?? DEFAULT_PACK_PALETTE;
}

// Derive a reasonable breadcrumb from the URL slug (e.g.
// `goblin_booster_pack` → `GOBLIN BOOSTER PACK`) so the header doesn't
// flash "PACK" for one render before the item definition lands.
function slugToBreadcrumb(slug: string): string {
  if (!slug) return "PACK";
  return slug.split("_").join(" ").toUpperCase();
}

// Reserved height: hero (420) × 1.15 (flipped) × 1.15 (pulse apex) ≈ 556;
// 580 leaves a small margin so the grid below never reflows on phase change.
const PHASE_AREA_HEIGHT = 580;

function packPanelStyle(palette: PackPalette) {
  return {
    background: palette.bg,
    border: `3px solid ${palette.trim}`,
    boxShadow: `inset 0 0 0 3px ${E32.ink}, 4px 4px 0 0 ${E32.ink}`,
    imageRendering: "pixelated" as const,
  };
}

function primaryButton(theme: typeof pixelTheme, disabled: boolean) {
  return withBtnStyle(primaryBtn(theme, disabled), {
    fontSize: 14,
    padding: "12px 20px",
  });
}

